import React, { createContext, useContext, useEffect, useState } from "react";
import { getPublishedCustomerPortal } from "_graphql/queries/customerPortal";
import { loadFont } from "_helpers/fontLoader";
import { isDarkColor } from "_helpers/colors";

const PortalSettingsContext = createContext();

// Create a provider component
export const PortalSettingsProvider = ({ children, client }) => {
  const [settings, setSettings] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Load portal settings
  const fetchSettings = async () => {
    try {
      // Use environment variable for company_id
      const companyId = process.env.REACT_APP_COMPANY_ID;

      let querySetup = {
        query: getPublishedCustomerPortal,
      };

      if (companyId) {
        querySetup.variables = {
          company_id: companyId
        };
      }
      
      const queryData = await client.query(querySetup);
      
      const portalData = queryData?.data?.getPublishedCustomerPortal;
      if (portalData) {
        // Store the company_id from the portal settings
        if (portalData.company_id) {
          setCompanyId(portalData.company_id);
        }
        await updateSettings(portalData);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error loading portal settings:", err);
      setLoading(false);
    }
  };

  // Update settings with font loading
  const updateSettings = async (newSettings) => {
    // Load custom font if specified
    if (newSettings?.primary_font && newSettings.primary_font !== settings?.primary_font) {
      try {
        await loadFont(newSettings.primary_font, "portal-primary-font");
        setFontsLoaded(true);
      } catch (err) {
        console.error("Error loading font:", err);
        setFontsLoaded(true); // Continue anyway
      }
    } else {
      setFontsLoaded(true);
    }

    setSettings(newSettings);
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update page title when settings change
  useEffect(() => {
    const displayName = settings?.title || settings?.company?.name;
    if (displayName) {
      document.title = `Customer Portal | ${displayName}`;
    }
  }, [settings?.title, settings?.company?.name]);

  // Update favicon when settings change
  useEffect(() => {
    if (settings?.favicon_url) {
      // Find existing favicon link or create a new one
      let faviconLink =
        document.querySelector("link[rel='icon']") ||
        document.querySelector("link[rel='shortcut icon']");

      if (!faviconLink) {
        faviconLink = document.createElement("link");
        faviconLink.rel = "icon";
        document.head.appendChild(faviconLink);
      }

      faviconLink.href = settings.favicon_url;
    }
  }, [settings?.favicon_url]);

  // Helper to get the title font
  const getTitleFont = () => {
    return settings?.primary_font || "Gambarino";
  };

  // Helper to get the primary color (for buttons)
  const getPrimaryColor = () => {
    return settings?.primary_color || "#292524"; // Default to stone-800
  };

  // Helper to get button text color based on primary color
  const getButtonTextColor = () => {
    const primaryColor = getPrimaryColor();
    return isDarkColor(primaryColor) ? "#FFFFFF" : "#000000";
  };

  // Update CSS variables when settings change (for fonts and colors)
  useEffect(() => {
    const root = document.documentElement;
    const titleFont = getTitleFont();
    const primaryColor = getPrimaryColor();
    const buttonTextColor = isDarkColor(primaryColor) ? "#FFFFFF" : "#000000";

    root.style.setProperty("--portal-title-font", titleFont);
    root.style.setProperty("--portal-primary-color", primaryColor);
    root.style.setProperty("--portal-button-text-color", buttonTextColor);
  }, [settings?.primary_font, settings?.primary_color]);

  return (
    <PortalSettingsContext.Provider
      value={{
        settings,
        companyId,
        loading,
        fontsLoaded,
        getTitleFont,
        getPrimaryColor,
        getButtonTextColor,
      }}
    >
      {children}
    </PortalSettingsContext.Provider>
  );
};

// Custom hook to use the context
export const usePortalSettings = () => {
  return useContext(PortalSettingsContext);
};
